
import axios, { AxiosRequestConfig, AxiosResponse, AxiosError, AxiosInstance, AxiosHeaders } from 'axios';
import { msalInstance } from '..';
import { loginRequest } from '../utilities/authConfig';
import { DotNetErrorParser } from '../utilities/errorParsers/DotNetErrorParser';
import { CorrelationUtils } from '../utilities/correlationUtils';
import { showErrorNotification } from '../utilities/messageUtils';
import { ApiErrorHandler } from '../utilities/apiTypes';

/**
 * Sets up request interceptor to add correlation ID if not already present
 * @param instance - The axios instance to configure
 * @param alwaysAddCorrelationId - If true, always add correlation ID even if one exists (default: false)
 */
const setupRequestInterceptor = (instance: AxiosInstance, alwaysAddCorrelationId: boolean = false) => {
  instance.interceptors.request.use(
    (config) => {
      if (!config.headers) {
        config.headers = new AxiosHeaders();
      }

      // Add correlation ID header only if not already present (unless alwaysAddCorrelationId is true)
      if (alwaysAddCorrelationId || !config.headers['x-correlation-id']) {
        const correlationId = CorrelationUtils.generateCorrelationId();
        config.headers['x-correlation-id'] = correlationId;
      }

      return config;
    },
    (error) => {
      console.error('Request interceptor error:', error);
      return Promise.reject(error);
    }
  );
};

/**
 * Sets up response interceptor for centralized error handling and display
 * @param instance - The axios instance to configure
 */
const setupResponseInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    (error: AxiosError) => {
      if (ApiErrorHandler.isCancelledRequest(error)) {
        return Promise.reject(error);
      }

      const requestMethod = (error.config?.method || 'GET').toUpperCase();
      const requestPath = error.config?.url && error.config.url.trim() ? error.config.url : '/';

      // Parse error with .NET-specific handler
      const baseUrl = `${process.env.REACT_APP_BACKEND_URI}`;
      const apiError = DotNetErrorParser.parseError(error, baseUrl);
      const requestTarget = apiError.url && apiError.url.trim() ? apiError.url : requestPath;

      // Log error details
      console.error('API Error (.NET Backend):', {
        url: apiError.url,
        statusCode: apiError.statusCode,
        message: apiError.message,
        correlationId: apiError.correlationId,
        timestamp: apiError.timestamp,
        backendSource: apiError.backendSource
      });

      // For authentication errors, we might want to handle token refresh here
      if (apiError.statusCode === 401) {
        console.warn('Authentication error detected. Token may need refresh.');
        // Could implement automatic token refresh logic here
        // Show a specific message for auth errors
        showErrorNotification(
          'Autentiseringsfel (.NET Backend)',
          apiError,
          {
            description: 'Din session har gått ut. Logga in igen.',
            duration: 30, // Persistent until closed
            showDetails: true
          }
        );
      } else {
        // Show error notification for all other errors
        // Determine a user-friendly title based on status code
        let errorTitle = 'Ett fel uppstod';
        let errorDescription: string | undefined;
        switch (apiError.statusCode) {
          case 400:
            errorTitle = 'Felaktig förfrågan';
            break;
          case 403:
            errorTitle = 'Åtkomst nekad';
            break;
          case 404:
            errorTitle = 'Resurs hittades inte';
            break;
          case 422:
            errorTitle = 'Valideringsfel';
            break;
          case 500:
          case 502:
          case 503:
            errorTitle = 'Serverfel';
            break;
          case 0:
            errorTitle = 'Nätverksfel';
            errorDescription = `Kunde inte nå .NET backend. Misslyckat anrop: ${requestMethod} ${requestTarget}. Kontrollera att backendtjänsten är igång.`;
            break;
          default:
            errorTitle = 'Ett fel uppstod';
        }

        showErrorNotification(
          errorTitle,
          apiError,
          {
            description: errorDescription,
            duration: 0, // Persistent until closed - user must acknowledge
            showDetails: true
          }
        );
      }

      // Reject with the standardized error format
      return Promise.reject(apiError);
    }
  );
};

/**
 * Sets up global axios interceptors for error handling
 * This should be called once during app initialization
 */
export const setupGlobalAxiosInterceptors = () => {
  // Set up interceptors for the default axios instance (used by SharepointService, etc.)
  setupRequestInterceptor(axios, false); // Don't override existing correlation IDs
  setupResponseInterceptor(axios);
};

// Initialize global interceptors immediately when this module is loaded
setupGlobalAxiosInterceptors();

// Create a dedicated axios instance for the backend
const backendAxios: AxiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URI}`,
  timeout: 30000, // 30 seconds timeout
});

// Set up interceptors for backendAxios (always add correlation ID for backend requests)
setupRequestInterceptor(backendAxios, true);
setupResponseInterceptor(backendAxios);

/**
 * BackendClient
 * 
 * A client to make requests to the backend with standardized error handling
 * 
 */
export class backendClient {
  /**
   * Get authentication headers with bearer token
   * @returns Promise with authorization headers
   */
  private static async getAuthHeaders(): Promise<{ Authorization: string }> {
    const account = msalInstance.getActiveAccount();
    if (!account) {
      throw new Error("No active account! Verify a user has been signed in and setActiveAccount has been called.");
    }

    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: account
    });

    return {
      Authorization: `Bearer ${response.accessToken}`,
    };
  }

  /**
   * Perform a GET and returns its response
   * @param url RequestInfo
   * @param config Headers and other configurations
   * @returns Promise with response
   * @template T The type of response can be set with .get<Type>(...)
   */
  public static async get<T = any>(
    url: string,
    config?: AxiosRequestConfig<any>
  ): Promise<AxiosResponse<T>> {
    const authHeaders = await this.getAuthHeaders();
    const erpIntegration = process.env.REACT_APP_ERP_INTEGRATION;

    return await backendAxios.get<T>(url, {
      ...config,
      headers: {
        ...authHeaders,
        ...(erpIntegration ? { 'x-erp-integration': erpIntegration } : {}),
        ...config?.headers
      }
    });
  }

  /**
   * Perform a POST and returns its response
   * @param url RequestInfo
   * @param data The data to send
   * @param config Headers and other configurations
   * @returns Promise with response
   * @template T The type of response can be set with .post<Type>(...)
   */
  public static async post<T = any>(
    url: string,
    data = {},
    config?: AxiosRequestConfig<any>
  ): Promise<AxiosResponse<T>> {
    const authHeaders = await this.getAuthHeaders();
    const erpIntegration = process.env.REACT_APP_ERP_INTEGRATION;

    return await backendAxios.post<T>(url, data, {
      ...config,
      headers: {
        ...authHeaders,
        ...(erpIntegration ? { 'x-erp-integration': erpIntegration } : {}),
        ...config?.headers
      }
    });
  }

  /**
   * Perform a PUT and returns its response
   * @param url RequestInfo
   * @param data The data to send
   * @param config Headers and other configurations
   * @returns Promise with response
   * @template T The type of response can be set with .put<Type>(...)
   */
  public static async put<T = any>(
    url: string,
    data = {},
    config?: AxiosRequestConfig<any>
  ): Promise<AxiosResponse<T>> {
    const authHeaders = await this.getAuthHeaders();
    const erpIntegration = process.env.REACT_APP_ERP_INTEGRATION;

    return await backendAxios.put<T>(url, data, {
      ...config,
      headers: {
        ...authHeaders,
        ...(erpIntegration ? { 'x-erp-integration': erpIntegration } : {}),
        ...config?.headers
      }
    });
  }

  /**
   * Perform a DELETE and returns its response
   * @param url RequestInfo
   * @param config Headers and other configurations
   * @returns Promise with response
   * @template T The type of response can be set with .delete<Type>(...)
   */
  public static async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig<any>
  ): Promise<AxiosResponse<T>> {
    const authHeaders = await this.getAuthHeaders();
    const erpIntegration = process.env.REACT_APP_ERP_INTEGRATION;

    return await backendAxios.delete<T>(url, {
      ...config,
      headers: {
        ...authHeaders,
        ...(erpIntegration ? { 'x-erp-integration': erpIntegration } : {}),
        ...config?.headers
      }
    });
  }

  /**
   * Perform a generic request - used by Orval-generated API client.
   * @param config Full Axios request config (url, method, data, etc.)
   * @returns Promise with response
   */
  public static async request<T = any>(
    config: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    const authHeaders = await this.getAuthHeaders();
    const erpIntegration = process.env.REACT_APP_ERP_INTEGRATION;

    return await backendAxios.request<T>({
      ...config,
      headers: {
        ...authHeaders,
        ...(erpIntegration ? { 'x-erp-integration': erpIntegration } : {}),
        ...config.headers
      }
    });
  }
}